/********************************************************************
 * Copyright (c) Certus Software, 2021.                             *
 *                                                                  *
 * This unpublished material is proprietary to Certus Software.     *
 * All rights reserved. The methods and techniques described        *
 * herein are considered confidential and/or trade secrets.         *
 * Reproduction or distribution, in whole or in part, is forbidden  *
 * except by express written permission of Certus Software.         *
 ********************************************************************/

import {BrowserModule, Title} from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import {UserService} from './@shared/_services/user.service';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CoreModule} from './@core/core.module';
import {SharedModule} from './@shared/shared.module';
import {AppRoutingModule} from './app-routing.module';
import {LicenseService} from './@shared/_services/license.service';
import {Location, UpperCasePipe} from '@angular/common';
import { ReportComponent } from './report/report.component';
import {NgxExtendedPdfViewerModule} from 'ngx-extended-pdf-viewer';
import {NgcCookieConsentConfig, NgcCookieConsentModule} from 'ngx-cookieconsent';
import {environment} from 'src/environments/environment';
import {HomeResolverService} from './@shared/_resolvers/home-resolver.service';
import {CookieService} from 'ngx-cookie-service';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {GoogleAnalyticsService} from './@shared/_services/google-analytics.service';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {HttpClient} from '@angular/common/http';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {BreadcrumbService} from 'xng-breadcrumb';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const cookieConfig: NgcCookieConsentConfig = {
  'cookie': {
    'domain': environment.cookie_domain,
    'name': 'gdpr'
  },
  /*'position': 'bottom-right',*/
  'palette': {
    'popup': {
      'background': '#2d353f',
      'text': '#ffffff',
      'link': '#ffffff'
    },
    'button': {
      'background': '#5ce3a7',
      'text': '#18583c',
      'border': 'transparent'
    }
  },
  'type': 'info',
  theme: 'block',
  layout: 'cookie-consent-layout',
  layouts: {
    'cookie-consent-layout': '{{messagelink}}{{customCompliance}}'
  },
  elements: {
    messagelink: `
    <span id="cookieconsent:desc" class="cc-message">{{message}}<br>
      <button tabindex="0" class="cc-link" target="_blank">{{privacyPolicyLabel}}</button>
    </span>
    `,
    customCompliance: `<div class="cc-compliance">
      <button tabindex="0" class="cc-btn cc-dismiss">{{dismiss}}</button>
    </div>`
  },
  content: {
    message: 'The website uses cookies which are essential for you to browse the website and use its features. We also use performance cookies, which collect traffic information but none of this information can be used to identify you. It is all aggregated and, therefore, anonymized. You can change the cookie settings at any time by clicking the Privacy settings link in the footer.',
    privacyPolicyLabel: 'Learn more about cookies',
    privacyPolicyHref: environment.app_url + '/download/products/cecewm/docs/Certus_Software_GmbH_Privacy_Policy.pdf',
    dismiss: 'I AGREE'
  }
};

@NgModule({
  declarations: [
    AppComponent,
    ReportComponent
  ],
  imports: [
    BrowserModule,
    CoreModule,
    SharedModule,
    NgxExtendedPdfViewerModule,
    BrowserAnimationsModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    AppRoutingModule,
    /* LANGUAGE CHANGE - CONFIGURE TRANSLATION HERE */
    TranslateModule.forRoot({
      /*defaultLanguage: (CookieService.prototype.get('CECEWM-LANGUAGE')) ? CookieService.prototype.get('CECEWM-LANGUAGE') : 'hu',*/
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ],
  providers: [
    UserService,
    LicenseService,
    CookieService,
    GoogleAnalyticsService,
    Location,
    Title,
    UpperCasePipe,
    HomeResolverService,
    { provide: 'eulaUrlResolver', useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        if ((route.data as any).lang === 'hu') {
          window.location.href = environment.app_url + '/download/products/cecewm/docs/Certus_Erasure_Vegfelhasznaloi_licencszerzodes.pdf';
        } else {
          window.location.href = environment.app_url + '/download/products/cecewm/docs/Certus_Erasure_End_User_License_Agreement.pdf';
        }
      }
    },
    BreadcrumbService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
